/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { Field } from '../../../../../components/FormComponents/Field'
import { NegationButton } from '../Components/NegationButton'
import { FieldDropdown } from '../../../../../components/FormComponents/FieldDropdown'
import Submit from '../Components/Submit'
import { Fieldset } from './Fieldset'
import { Form } from './Form'
import LabelForDueDateField from 'src/features/receivement/components/LabelForDueDateField'
import InformationField from 'src/features/receivement/components/InformationField'
import ModalAlert from 'src/components/ModalAlert'
import { Typography } from '@mui/material'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import * as S from './style'

const defaultCountSchema = yup
  .number()
  .required('Campo obrigatório!')
  .integer('Quantidade deve ser um número inteiro!')
  .min(1, 'Valor deve ser no mínimo 1!')

const schema = yup.object().shape({
  boxQty: defaultCountSchema,
  productBoxQty: defaultCountSchema,
  singleQty: yup
    .number()
    .required('Campo obrigatório!')
    .integer('Quantidade deve ser um número inteiro!')
    .min(0, 'Valor deve ser no mínimo 1!'),
  damageType: yup.string().required('Campo obrigatório!'),
  damageQty: yup
    .number()
    .required('Campo obrigatório!')
    .when('damageType', {
      is: 'NENHUM',
      then: yup.number().equals([0], 'Valor deve ser 0!'),
      otherwise: yup.number().min(1, 'Valor deve ser no mínimo 1!')
    })
    .when(
      ['boxQty', 'productBoxQty', 'singleQty'],
      (boxQty, productBoxQty, singleQty, schema) => {
        const total = boxQty * productBoxQty + singleQty
        return schema.max(
          total,
          `Quantidade de avaria deve ser menor ou igual a total`
        )
      }
    ),
  expirationDate: yup
    .date()
    .required('Campo obrigatório!')
})

const FormAddNotFoundBatch = ({ onSuccess, onCancel, deliveryDaysMinExpirationDate }) => {
  const { control, handleSubmit, setFocus, watch, formState, reset, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      damageType: 'NENHUM',
      damageQty: '0',
    },
  })

  const boxQty = watch('boxQty', 0)
  const productBoxQty = watch('productBoxQty', 0)
  const singleQty = watch('singleQty', 0)
  const totalQty = watch('total', 0)
  const damageQty = watch('damageQty', 0)

  const dueDate = watch('expirationDate')
  const [dueDateType, setDueDateType] = useState(0)

  const dueDateTypeInfo = useMemo(() => {
    switch (dueDateType) {
      case 1:
        return {
          title: 'Atenção: Validade próxima!',
          alertIconColor: '#FFC400',
          message: 'Atenção: Esse lote está próximo da validade!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FFC400' tooltipMessage='Atenção: Validade baixa' />,
          damage: false
        }
      case 2:
        return {
          title: 'Atenção: Produto vencido!',
          alertIconColor: '#FF0000',
          message: 'Atenção: Esse lote está vencido!',
          fieldLabel: <LabelForDueDateField showAlert alertColor='#FF0000' tooltipMessage='Atenção: Produto vencido' />,
          damage: true
        }
      default:
        return {
          title: '',
          alertIconColor: '#000',
          message: '',
          fieldLabel: <LabelForDueDateField showAlert={false} />,
          damage: false
        }
    }
  }, [dueDateType])

  const resetDueDateAndDamageFields = (newDueDateType) => {
    if (dueDateType === 2 && newDueDateType !== 2) {
      setValue('damageType', 'NENHUM')
      setValue('damageQty', '0')
    }
    setDueDateType(newDueDateType)
  }

  const [showModalAlertDueDate, setShowModalAlertDueDate] = useState(false)

  useEffect(() => {
    if (dueDate) {
      const inputDueDate = new Date(dueDate)
      inputDueDate.setHours(inputDueDate.getHours() + (inputDueDate.getTimezoneOffset() / 60))

      const currentDate = new Date()
      currentDate.setHours(0, 0, 0, 0)

      if (inputDueDate < currentDate) {
        setDueDateType(2)
        setValue('damageType', 'VENCIDO')
        setValue('damageQty', totalQty)
        return
      }

      if (inputDueDate <= currentDate.setDate(currentDate.getDate() + deliveryDaysMinExpirationDate)) {
        resetDueDateAndDamageFields(1)
        return
      }

      resetDueDateAndDamageFields(0)
      return
    }

    resetDueDateAndDamageFields(0)
  }, [dueDate])

  const onSubmit = (data) => {
    if (dueDateType !== 0 && !showModalAlertDueDate) {
      setShowModalAlertDueDate(true)
      return
    }

    try {
      const total = data.boxQty * data.productBoxQty + data.singleQty
      const batchAdded = {
        ...data,
        isKg: false,
        total
      }

      reset()
      onSuccess(batchAdded)
    } catch (err) {
      console.error(err)
    } finally {
      setShowModalAlertDueDate(false)
    }
  }

  const enterKeyCode = 13
  const tabKeyCode = 9

  const handleOnKeyDown = (event, nextField) => {
    if ([enterKeyCode, tabKeyCode].includes(event.keyCode) && !event.shiftKey) {
      event.preventDefault()
      setFocus(nextField)
    }
  }

  const formatterBR = useCallback((boxQty, productBoxQty, singleQty) => {
    return (
      ((boxQty && productBoxQty) || singleQty) &&
      new Intl.NumberFormat('pt-BR').format(
        parseInt(boxQty || 0) * parseInt(productBoxQty || 0) +
          parseInt(singleQty || 0)
      )
    )
  }, [])

  useEffect(() => {
    const total = formatterBR(boxQty, productBoxQty, singleQty) || ''

    setValue('total', total)

    if (dueDateTypeInfo.damage) {
      setValue('damageQty', total)
    }
  }, [boxQty, productBoxQty, singleQty, setValue])

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Fieldset>
          <Field
            labelText="Qtd. caixas"
            name="boxQty"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, 'productBoxQty')}
            autoFocus
            data-testid='boxes-qty-test-id'
          />
          <Field
            labelText="Caixaria"
            name="productBoxQty"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, 'singleQty')}
            data-testid='box-qty-test-id'
          />
        </Fieldset>
        <Fieldset>
          <Field
            labelText="Avulso"
            name="singleQty"
            control={control}
            type="number"
            onKeyDown={(e) => handleOnKeyDown(e, 'expirationDate')}
            data-testid='single-qty-test-id'
          />
          <Field
            labelText="Total"
            name="total"
            control={control}
            readOnly
            value={totalQty}
            onKeyDown={(e) => handleOnKeyDown(e, 'expirationDate')}
            data-testid='total-qty-test-id'
          />
        </Fieldset>
        <Fieldset>
          <Field
            name="expirationDate"
            control={control}
            labelText={dueDateTypeInfo.fieldLabel}
            type="date"
            inputTextColor={dueDateTypeInfo.damage ? 'red' : 'black'}
            onKeyDown={(e) => handleOnKeyDown(e, dueDateTypeInfo.damage ? 'damageQty' : 'damageType')}
            data-testid='expiration-date-test-id'
          />
          {dueDateTypeInfo.damage ? (
            <InformationField
              title='Tipo Avaria'
              info='VENCIDO'
              tooltipInfoMessage='Lote definido automaticamente como vencido'
              testId='automatic-damage-type-test-id'
              sxProps={{
                container: {
                  gap: '.1rem',
                  marginTop: '.15rem',
                },
                title: {
                  fontSize: '16px',
                  paddingLeft: '.2rem',
                },
                containerInfo: {
                  backgroundColor: '#F0F0F0',
                },
              }}
            />
          ) : (
            <FieldDropdown
              labelText="Tipo avaria"
              name="damageType"
              options={['NENHUM', 'DANO']}
              control={control}
              onKeyDown={(e) => handleOnKeyDown(e, 'damageQty')}
              data-testid='damage-type-test-id'
            />
          )}
          <Field
            labelText="Qtd. avaria"
            name="damageQty"
            control={control}
            type="number"
            readOnly={dueDateTypeInfo.damage}
            value={damageQty}
            style={{ width: '115px' }}
            data-testid='damage-qty-test-id'
          />
        </Fieldset>

        <S.ContainerButtons>
          <Submit
            textWhenEnabled="Incluir"
            textWhenDisabled="Salvando..."
            disabled={formState.isSubmitting}
          />
          <NegationButton
            title="Cancelar"
            onClick={onCancel}
            disabled={formState.isSubmitting}
          />
        </S.ContainerButtons>
      </Form>

      {dueDateType !== 0 && (
        <ModalAlert
          show={showModalAlertDueDate}
          title={dueDateTypeInfo.title}
          centered
          action={handleSubmit(onSubmit)}
          okButtonDisabled={formState.isSubmitting}
          closeButtonAction={() => setShowModalAlertDueDate(false)}
          closeButtonDisabled={formState.isSubmitting}
          customizableBody={
            <S.ModalAlertDueDateContainer>
              <ReportProblemIcon
                sx={{
                  fontSize: 80,
                  color: dueDateTypeInfo.alertIconColor
                }}
                data-testid='modal-alert-icon-test-id'
              />
              <S.ModalAlertDueDateInfo>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  {dueDateTypeInfo.message}
                </Typography>
                <Typography variant='body1' color='textPrimary' sx={{ fontWeight: 600 }}>
                  Guarde-o separadamente pois ele poderá ser devolvido pelo time de compras!
                </Typography>
              </S.ModalAlertDueDateInfo>
            </S.ModalAlertDueDateContainer>
          }
          testId='modal-due-date-alert-test-id'
        />
      )}
    </>
  )
}

export { FormAddNotFoundBatch }
